import { useQueries, useQuery } from "@tanstack/react-query";
import axios from "axios";

const EMPTY_ACCOUNTS = [];

/**
 * Fetch all marketplace accounts for a given store.
 *
 * Backend: GET /api/stores/{storeId}/marketplace-accounts
 * (PR 2 — Multi-store Chrono24 — see retail-circle.com PR #1627)
 *
 * The list is cached for 5 minutes. Each row includes:
 *   id, store_id_m, marketplace, label, slug, currency, is_enabled
 */
export const useMarketplaceAccounts = (storeIdM, { enabled = true } = {}) => {
  const query = useQuery({
    queryKey: ["marketplace-accounts", storeIdM],
    queryFn: async () => {
      const { data } = await axios.get(
        `/api/stores/${storeIdM}/marketplace-accounts`
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: enabled && !!storeIdM,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  return {
    accounts: query.data ?? EMPTY_ACCOUNTS,
    status:   query.status,
    error:    query.error,
    refetch:  query.refetch,
  };
};

/**
 * Filtered helper — enabled Chrono24 accounts only, sorted with the
 * legacy slug=null row (DAO-UK) first to preserve UI ordering.
 */
export const useChrono24Accounts = (storeIdM, opts) => {
  const { accounts, status, error, refetch } = useMarketplaceAccounts(storeIdM, opts);

  const chrono24 = accounts
    .filter((a) => a.marketplace === "chrono24" && a.is_enabled)
    .sort((a, b) => {
      if (a.slug === null && b.slug !== null) return -1;
      if (b.slug === null && a.slug !== null) return 1;
      return (a.label || "").localeCompare(b.label || "");
    });

  return { accounts: chrono24, status, error, refetch };
};

const fetchListingMarketplaceState = async (listingId, accountId) => {
  const { data } = await axios.get(`/api/listings/${listingId}/marketplaces/${accountId}`);
  return data;
};

/**
 * Fetch a single per-listing × per-account state row. Cached per
 * `(listingId, accountId)` so multiple cards on the same page reuse it.
 *
 * Backend: GET /api/listings/{listing}/marketplaces/{account}
 */
export const useListingMarketplaceState = (listingId, accountId, { enabled = true } = {}) => {
  const query = useQuery({
    queryKey: ["listing-marketplace-state", listingId, accountId],
    queryFn: () => fetchListingMarketplaceState(listingId, accountId),
    enabled: enabled && !!listingId && !!accountId,
    staleTime: 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return {
    state:   query.data,
    status:  query.status,
    error:   query.error,
    refetch: query.refetch,
  };
};

/**
 * The Chrono24 accounts this listing is switched ON for — what the feed
 * actually carries, so what a "Remove & Repost" would repost. The legacy
 * DAO-UK account (slug === null) reads listing.is_chrono; every other account
 * reads its junction row. Same query keys as useListingMarketplaceState, so
 * the Chrono24 tab dots and this share one cache entry per account.
 */
export const useListedChrono24Accounts = (listing, accounts = EMPTY_ACCOUNTS) => {
  const listingId = listing?.id;
  const results = useQueries({
    queries: accounts.map((account) => ({
      queryKey: ["listing-marketplace-state", listingId, account.id],
      queryFn: () => fetchListingMarketplaceState(listingId, account.id),
      enabled: !!listingId && account.slug !== null,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
    })),
  });

  return accounts.filter((account, i) =>
    account.slug === null ? !!listing?.is_chrono : !!results[i]?.data?.is_listed
  );
};
